/**
 * 自定义 Tooltip 模块
 * 提供简单易用的 tooltip 功能，支持桌面端和移动端
 */

interface TooltipOptions {
  /** 是否在移动端显示关闭按钮 */
  showCloseButton?: boolean;
  /** 自定义 z-index 值 */
  zIndex?: number;
  /** tooltip 的背景颜色 */
  backgroundColor?: string;
  /** tooltip 的文字颜色 */
  textColor?: string;
}

class CustomTooltip {
  private tooltip: HTMLDivElement;
  private options: TooltipOptions;
  private isInitialized: boolean = false;

  constructor(options: TooltipOptions = {}) {
    this.options = {
      showCloseButton: true,
      zIndex: 100000,
      backgroundColor: '#dddddd',
      textColor: '#000000',
      ...options
    };

    this.tooltip = document.createElement('div');
    this.tooltip.className = 'custom-tooltip';
  }

  /**
   * 初始化 tooltip
   */
  init() {
    if (this.isInitialized) return;

    document.body.appendChild(this.tooltip);
    this.addStyles();
    this.setupEventListeners();
    this.isInitialized = true;
  }

  /**
   * 添加必要的样式
   */
  private addStyles() {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      .custom-tooltip {
        display: none;
        position: fixed;
        background-color: ${this.options.backgroundColor};
        color: ${this.options.textColor};
        padding: 20px;
        width: 200px;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        text-align: center;
        font-size: 12px;
        line-height: 1.4;
        z-index: ${this.options.zIndex};
        cursor: pointer;
      }
      .tooltip-arrow {
        position: absolute;
        width: 0;
        height: 0;
        bottom: -10px;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid ${this.options.backgroundColor};
      }
        .gap-2 {
          gap: 0.5rem;
        }

      /* 桌面端arrow位置由JavaScript动态设置 */
      @media (min-width: 768px) {
        .tooltip-arrow {
          /* 位置由JavaScript动态设置，这里不设置固定位置 */
        }
      }
      .tooltip_info {
        margin-left: 5px;
        margin-top: -2px;
        cursor: pointer !important;
        display: inline-block;
      }
      @media (max-width: 767px) {
        .custom-tooltip {
        }
        .tooltip-arrow {
          display: block;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * 设置事件监听器
   */
  private setupEventListeners() {
    const isMobile = () => window.innerWidth < 768;

    // 处理移动端触摸事件
    const handleTouchStart = (e: TouchEvent) => {
      if (isMobile() && this.tooltip.style.display === 'block') {
        const target = e.target as Element;
        if (!target.closest('.tooltip_info') && !target.closest('.custom-tooltip')) {
          this.hide();
        }
      }
    };

    // 处理桌面端点击事件
    const handleClick = (e: MouseEvent) => {
      if (!isMobile() && this.tooltip.style.display === 'block') {
        const target = e.target as Element;
        if (!target.closest('.tooltip_info') && !target.closest('.custom-tooltip')) {
          this.hide();
        }
      }
    };

    // 添加事件监听器
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('click', handleClick);

    // 处理窗口大小变化时隐藏tooltip
    window.addEventListener('resize', () => {
      this.hide();
    });

    // 处理页面滚动时隐藏tooltip（移动端）
    window.addEventListener('scroll', () => {
      if (isMobile()) {
        this.hide();
      }
    }, { passive: true });

    document.querySelector('#chart-stage')?.addEventListener('touchstart', () => {
      this.hide();
    });
  }

  /**
   * 为元素添加 tooltip 功能
   * @param element 需要添加 tooltip 的元素
   * @param text tooltip 显示的文本
   */
  attach(element: HTMLElement, text: string) {
    if (!this.isInitialized) {
      this.init();
    }

    // 移除可能存在的旧属性
    // element.removeAttribute('data-toggle');
    // element.removeAttribute('data-placement');
    // element.removeAttribute('title');

    // 设置新属性
    // element.setAttribute('data-tooltip', text);
    // element.classList.add('tooltip_info');

    // 添加事件监听
    element.addEventListener('mouseenter', () => this.show(element, text));
    element.addEventListener('mouseleave', () => this.hide());
    element.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.show(element, text);
    });
  }

  /**
   * 显示 tooltip
   * @param element 触发元素
   * @param text tooltip 文本
   */
  show(element: HTMLElement, text: string) {
    const isMobile = window.innerWidth < 768;

    // 添加小三角形指示器
    const arrow = '<span class="tooltip-arrow"></span>';
    this.tooltip.innerHTML = text + arrow;

    // 计算位置
    const rect = element.getBoundingClientRect();

    // if (isMobile) {
    //   this.tooltip.style.left = "15px";
    //   this.tooltip.style.top = (rect.top - 8 - this.tooltip.offsetHeight) + "px";

    //   // 如果 tooltip 已经渲染但高度为 0（第一次显示），需要在下一帧重新定位
    //   if (this.tooltip.offsetHeight === 0) {
    //     setTimeout(() => {
    //       this.tooltip.style.top = (rect.top - 8 - this.tooltip.offsetHeight) + "px";
    //       this.positionArrow(element);
    //     }, 0);
    //   } else {
    //     this.positionArrow(element);
    //   }

    //   this.tooltip.style.transform = "none";
    // } else {
      // 桌面端显示在元素上方
      const tooltipWidth = 200; // 预估宽度，可以根据实际情况调整
      let leftPosition = rect.left + (rect.width / 2) - (tooltipWidth / 2) + window.scrollX;
      
      // 检查右边界，防止tooltip超出屏幕右侧
      const viewportWidth = window.innerWidth;
      const tooltipRightEdge = leftPosition + tooltipWidth;
      
      if (tooltipRightEdge > viewportWidth) {
        // 调整位置，使tooltip右边缘贴合视窗右边缘，留出少许边距
        leftPosition = viewportWidth - tooltipWidth - 10;
      }
      
      // 同样检查左边界，防止调整后超出左侧
      if (leftPosition < 10) {
        leftPosition = 10;
      }
    
      this.tooltip.style.left = leftPosition + "px";
      this.tooltip.style.top = (rect.top - 10 - this.tooltip.offsetHeight) + "px";

      // 如果 tooltip 已经渲染但高度为 0（第一次显示），需要在下一帧重新定位
      if (this.tooltip.offsetHeight === 0) {
        setTimeout(() => {
          this.tooltip.style.top = (rect.top - 10 - this.tooltip.offsetHeight) + "px";
          this.positionArrow(element);
        }, 0);
      } else {
        this.positionArrow(element);
      }

      this.tooltip.style.transform = "none";
    // }

    this.tooltip.style.display = "block";
  }

  /**
   * 动态定位arrow位置，使其对准触发图标
   * @param element 触发元素
   */
  private positionArrow(element: HTMLElement) {
    const arrow = this.tooltip.querySelector('.tooltip-arrow') as HTMLElement;
    if (!arrow) return;

    const elementRect = element.getBoundingClientRect();
    const tooltipRect = this.tooltip.getBoundingClientRect();

    // 计算图标相对于tooltip的横向位置
    const elementCenter = elementRect.left + (elementRect.width / 2);
    const tooltipLeft = tooltipRect.left;
    const tooltipWidth = tooltipRect.width;

    // 计算arrow应该在tooltip中的位置（相对于tooltip左边的像素值）
    let arrowLeft = elementCenter - tooltipLeft;

    // 确保arrow不会超出tooltip边界（留10px边距）
    const minLeft = 20; // 箭头最小左边距
    const maxLeft = tooltipWidth - 20; // 箭头最大左边距

    arrowLeft = Math.max(minLeft, Math.min(maxLeft, arrowLeft));

    // 设置arrow位置
    arrow.style.left = `${arrowLeft}px`;
    arrow.style.marginLeft = '-10px'; // 箭头宽度的一半，用于居中
  }

  /**
   * 隐藏 tooltip
   */
  hide() {
    this.tooltip.style.display = "none";
  }

  /**
   * 批量为所有带有 data-tooltip 属性的元素添加 tooltip 功能
   * @param selector 可选的选择器，默认为 '.tooltip_info'
   * @param container 可选的容器元素，默认为 document
   */
  attachAll(selector: string = '.tooltip_info', container: Document | Element = document) {
    if (!this.isInitialized) {
      this.init();
    }

    const elements = container.querySelectorAll(selector);
    elements.forEach((elem) => {
      const tooltipText = elem.getAttribute('data-tooltip');
      if (tooltipText) {
        this.attach(elem as HTMLElement, tooltipText);
      }
    });
  }

  /**
   * 创建带有 tooltip 的信息图标
   * @param tooltipText tooltip 文本
   * @returns HTML 字符串
   */
  createInfoIcon(tooltipText: string): string {
    return `<span class="icon-svg icon-svg--size-14 tooltip_info hand" data-tooltip="${tooltipText}">
      <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" viewBox="0 0 16 16">
        <circle cx="8" cy="8" r="8" fill="#5D4294"/>
        <path d="M8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zM7.25 7a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-1.5 0V7z" fill="#ffffff"/>
      </svg>
    </span>`;
  }
}

export default CustomTooltip;
